import React, { useState, useCallback } from 'react';
import useTranslation from '../../hooks/useTranslation';
import { store } from '../../App';
import { setHideHeaderLinks } from '../../Redux/OverlayActions';
import './Onboarding.css';

function Onboarding({ onComplete }) {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [touchStartX, setTouchStartX] = useState(0);
    const [touchEndX, setTouchEndX] = useState(0);
    const { t, branding, images } = useTranslation();

    // Get translated content
    const onboardingConfig = t('onboarding', {});
    const companyName = t('company.name', 'Foubert');
    const productName = t('company.productName', 'ijskar');
    const productNamePlural = t('company.productNamePlural', 'ijskarren');

    // Total slides including consent slide
    const totalSlides = 5;

    const nextSlide = useCallback(() => {
        if (currentSlide < totalSlides - 1) {
            setCurrentSlide(prev => prev + 1);
        }
    }, [currentSlide]);

    const prevSlide = useCallback(() => {
        if (currentSlide > 0) {
            setCurrentSlide(prev => prev - 1);
        }
    }, [currentSlide]);

    const goToSlide = useCallback((index) => {
        setCurrentSlide(index);
    }, []);

    const handleAcceptConsent = useCallback(() => {
        localStorage.setItem('onboardingCompleted', 'true');
        localStorage.setItem('consent', 'true');
        store.dispatch(setHideHeaderLinks(false));
        if (onComplete) {
            onComplete();
        }
    }, [onComplete]);

    // Skip goes to consent slide, not complete
    const handleSkipToConsent = useCallback(() => {
        setCurrentSlide(totalSlides - 1);
    }, []);

    const handleTouchStart = useCallback((e) => {
        setTouchStartX(e.touches[0].clientX);
    }, []);

    const handleTouchMove = useCallback((e) => {
        setTouchEndX(e.touches[0].clientX);
    }, []);

    const handleTouchEnd = useCallback(() => {
        const swipeThreshold = 50;

        if (touchStartX - touchEndX > swipeThreshold) {
            nextSlide();
        } else if (touchEndX - touchStartX > swipeThreshold) {
            prevSlide();
        }
    }, [touchStartX, touchEndX, nextSlide, prevSlide]);

    // Get images from shared config
    const logoSrc = images?.logo || '/images/logo.png';
    const callButtonImage = images?.callButtonImage || '/images/tracker/icon_button.png';
    const callButtonOverlay = images?.callButtonOverlay || '/images/tracker/icon_roep.png';

    // Get branding colors
    const secondaryColor = branding?.secondaryColor || '#62B5E5';
    const accentColor = branding?.accentColor || '#ffd700';
    const textColor = branding?.textColor || '#4a3728';

    // Get slide texts from config or use defaults
    const slideTexts = onboardingConfig.slides || [];
    const welcomeSlide = slideTexts[0] || {};
    const trackSlide = slideTexts[1] || {};
    const callSlide = slideTexts[2] || {};
    const notifySlide = slideTexts[3] || {};

    // Get consent and navigation texts
    const consentConfig = onboardingConfig.consent || {};
    const navConfig = onboardingConfig.navigation || {};

    const slides = [
        // Slide 0: Welcome
        <div className="onboarding-slide welcome-slide" key="welcome">
            <div className="slide-content">
                <div className="welcome-icon">
                    <img src={logoSrc} alt={companyName} className="welcome-logo" />
                </div>
                <h1>{welcomeSlide.title || `Welkom bij ${companyName}!`}</h1>
                <p className="welcome-text">
                    {welcomeSlide.subtitle || `Ontdek hoe je onze ${productNamePlural} kan volgen en zelfs naar jou kan laten komen.`}
                </p>
                <div className="swipe-hint">
                    <span>{welcomeSlide.swipeHint || 'Swipe om te ontdekken'}</span>
                    <div className="swipe-arrow">→</div>
                </div>
            </div>
        </div>,

        // Slide 1: Track trucks
        <div className="onboarding-slide feature-slide" key="track">
            <div className="slide-content">
                <div className="feature-illustration">
                    <div className="feature-icon track-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/>
                            <path d="M12 6v6l4 2"/>
                        </svg>
                    </div>
                </div>
                <h2>{trackSlide.title || `Volg onze ${productNamePlural}`}</h2>
                <p dangerouslySetInnerHTML={{
                    __html: trackSlide.description || `Bekijk in realtime waar onze ${productNamePlural} rijden. <strong>Tik op een ${productName}</strong> om te zien wat ze aan het doen zijn en of ze beschikbaar zijn.`
                }} />
                <div className="feature-preview">
                    <svg className="preview-truck-svg" viewBox="0 0 80 80" width="80" height="80">
                        <defs>
                            <linearGradient id="truckGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stopColor={secondaryColor}/>
                                <stop offset="100%" stopColor="#4aa3d5"/>
                            </linearGradient>
                            <filter id="truckShadow" x="-20%" y="-20%" width="140%" height="140%">
                                <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.3"/>
                            </filter>
                        </defs>
                        <circle cx="40" cy="40" r="28" fill="url(#truckGradient)" filter="url(#truckShadow)"/>
                        <text x="40" y="48" textAnchor="middle" fill="white" fontSize="24" fontWeight="700" fontFamily="DM Sans, sans-serif">1</text>
                    </svg>
                    <svg className="preview-status-svg" viewBox="0 0 40 40" width="40" height="40">
                        <defs>
                            <linearGradient id="availableGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stopColor="#4CAF50"/>
                                <stop offset="100%" stopColor="#45a049"/>
                            </linearGradient>
                        </defs>
                        <circle cx="20" cy="20" r="14" fill="url(#availableGradient)" stroke="white" strokeWidth="3"/>
                        <path d="M14 20 L18 24 L26 16" stroke="white" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </div>
            </div>
        </div>,

        // Slide 2: Call trucks - Using actual button images
        <div className="onboarding-slide feature-slide" key="call">
            <div className="slide-content">
                <div className="feature-illustration">
                    <div className="feature-icon call-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                        </svg>
                    </div>
                </div>
                <h2>{callSlide.title || `Roep de ${productName}`}</h2>
                <p dangerouslySetInnerHTML={{
                    __html: callSlide.description || `Wil je een ijsje? <strong>Steek virtueel je hand op!</strong> Druk op de grote rode knop om te laten weten dat je een ijsje wil. Als de ${productName} kan komen, komt hij langs!`
                }} />
                <div className="feature-preview call-preview">
                    <div className="call-button-preview">
                        <img src={callButtonImage} alt="Call button" className="preview-call-btn-bg" />
                        <img src={callButtonOverlay} alt="Roep" className="preview-call-btn-overlay" />
                    </div>
                </div>
            </div>
        </div>,

        // Slide 3: Notifications
        <div className="onboarding-slide feature-slide" key="notify">
            <div className="slide-content">
                <div className="feature-illustration">
                    <div className="feature-icon notify-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                            <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                        </svg>
                    </div>
                </div>
                <h2>{notifySlide.title || 'Blijf op de hoogte'}</h2>
                <p dangerouslySetInnerHTML={{
                    __html: notifySlide.description || `Wil je gewaarschuwd worden als er een <strong>beschikbare ${productName} in de buurt</strong> is? Activeer de radar door op het icoon rechtsonder te tikken.`
                }} />
                <div className="feature-preview notify-preview">
                    <svg className="preview-radar-btn-svg" viewBox="0 0 60 60" width="60" height="60">
                        <defs>
                            <linearGradient id="radarGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stopColor={accentColor}/>
                                <stop offset="100%" stopColor="#ffb800"/>
                            </linearGradient>
                            <filter id="radarShadow" x="-20%" y="-20%" width="140%" height="140%">
                                <feDropShadow dx="0" dy="3" stdDeviation="3" floodColor="#ffb800" floodOpacity="0.4"/>
                            </filter>
                        </defs>
                        <circle cx="30" cy="30" r="25" fill="url(#radarGradient)" filter="url(#radarShadow)"/>
                        <path d="M30 18 C36 18 42 24 42 30" stroke={textColor} strokeWidth="2.5" fill="none" strokeLinecap="round" opacity="0.8"/>
                        <path d="M30 24 C34 24 38 28 38 32" stroke={textColor} strokeWidth="2.5" fill="none" strokeLinecap="round" opacity="0.6"/>
                        <circle cx="30" cy="32" r="4" fill={textColor}/>
                    </svg>
                    <span className="preview-label">{navConfig.notificationHint || 'Tik hier voor meldingen'}</span>
                </div>
            </div>
        </div>,

        // Slide 4: Consent / Terms
        <div className="onboarding-slide consent-slide" key="consent">
            <div className="slide-content">
                <div className="feature-illustration">
                    <div className="feature-icon consent-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M9 12l2 2 4-4"/>
                            <path d="M12 3c7.2 0 9 1.8 9 9s-1.8 9-9 9-9-1.8-9-9 1.8-9 9-9z"/>
                        </svg>
                    </div>
                </div>
                <h2>{consentConfig.title || 'Voorwaarden'}</h2>
                <p>
                    {consentConfig.description || 'Om deze app te gebruiken moet je akkoord gaan met onze voorwaarden.'}
                </p>
                <div className="consent-links">
                    <a href="/privacypolicy" target="_blank" rel="noreferrer" className="consent-link">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                        </svg>
                        {consentConfig.privacyPolicy || 'Privacyverklaring'}
                    </a>
                    <a href="/cookiepolicy" target="_blank" rel="noreferrer" className="consent-link">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/>
                            <circle cx="8" cy="9" r="1.5" fill="currentColor"/>
                            <circle cx="15" cy="8" r="1" fill="currentColor"/>
                            <circle cx="10" cy="14" r="1.5" fill="currentColor"/>
                            <circle cx="16" cy="13" r="1" fill="currentColor"/>
                        </svg>
                        {consentConfig.cookiePolicy || 'Cookievoorwaarden'}
                    </a>
                </div>
            </div>
        </div>,
    ];

    return (
        <div className="onboarding-overlay">
            <div
                className="onboarding-container"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                <div
                    className="onboarding-slides"
                    style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                >
                    {slides}
                </div>

                {/* Dots indicator */}
                <div className="onboarding-dots">
                    {slides.map((_, index) => (
                        <button
                            key={index}
                            className={`dot ${currentSlide === index ? 'active' : ''}`}
                            onClick={() => goToSlide(index)}
                            aria-label={`Ga naar slide ${index + 1}`}
                        />
                    ))}
                </div>

                {/* Navigation buttons */}
                <div className="onboarding-nav">
                    {currentSlide > 0 && currentSlide < slides.length - 1 && (
                        <button className="nav-btn prev-btn" onClick={prevSlide}>
                            ←
                        </button>
                    )}

                    {currentSlide < slides.length - 1 ? (
                        <button className="nav-btn next-btn" onClick={nextSlide}>
                            {navConfig.next || 'Volgende'} →
                        </button>
                    ) : (
                        <button className="nav-btn start-btn consent-btn" onClick={handleAcceptConsent}>
                            {consentConfig.acceptButton || 'Aanvaard de voorwaarden'}
                        </button>
                    )}
                </div>

                {/* Skip button - goes to consent slide, not skip entirely */}
                {currentSlide < slides.length - 1 && (
                    <button className="skip-btn" onClick={handleSkipToConsent}>
                        {navConfig.skip || 'Overslaan'}
                    </button>
                )}
            </div>
        </div>
    );
}

export default Onboarding;
